import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  shouldUsePresignedUpload,
  requestPresignedUrls,
  uploadFileToS3,
} from "../api/presignedUpload";
import {
  FileUploadProgress,
  PresignedUploadState,
} from "../models/presignedUpload";

export interface UploadResult {
  usedPresigned: boolean;
  uploadIds: string[];
  files: File[];
}

export function usePresignedUpload() {
  const [state, setState] = useState<PresignedUploadState>({
    isUploading: false,
    files: [],
    overallProgress: 0,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const updateFileProgress = useCallback(
    (filename: string, updates: Partial<FileUploadProgress>) => {
      setState((prev) => {
        const updatedFiles = prev.files.map((f) =>
          f.filename === filename ? { ...f, ...updates } : f,
        );

        // Calculate overall progress
        const totalProgress = updatedFiles.reduce(
          (sum, f) => sum + f.progress,
          0,
        );
        const overallProgress =
          updatedFiles.length > 0
            ? Math.round(totalProgress / updatedFiles.length)
            : 0;

        return {
          ...prev,
          files: updatedFiles,
          overallProgress,
        };
      });
    },
    [],
  );

  const uploadFiles = useCallback(
    async (files: File[]): Promise<UploadResult> => {
      // Check if we should use presigned upload
      if (!shouldUsePresignedUpload(files)) {
        return {
          usedPresigned: false,
          uploadIds: [],
          files,
        };
      }

      // Initialize upload state
      const initialFileStates: FileUploadProgress[] = files.map((file) => ({
        filename: file.name,
        uploadId: "",
        progress: 0,
        status: "pending",
      }));

      setState({
        isUploading: true,
        files: initialFileStates,
        overallProgress: 0,
      });

      abortControllerRef.current = new AbortController();

      try {
        // Build file info with content types
        const fileInfos = files.map((f) => ({
          filename: f.name,
          contentType: f.type || "application/octet-stream",
        }));

        // Step 1: Request presigned URLs
        const presignedUrls = await requestPresignedUrls(fileInfos);

        // Update file states with upload IDs (use index to handle duplicate filenames)
        setState((prev) => ({
          ...prev,
          files: prev.files.map((f, index) => {
            const presigned = presignedUrls[index];
            return presigned ? { ...f, uploadId: presigned.uploadId } : f;
          }),
        }));

        // Step 2: Upload files to S3 in parallel (use index to handle duplicate filenames)
        const uploadPromises = files.map(async (file, index) => {
          const presigned = presignedUrls[index];
          const fileInfo = fileInfos[index];

          if (!presigned) {
            throw new Error(`No presigned URL for file: ${file.name}`);
          }

          updateFileProgress(file.name, { status: "uploading" });

          try {
            await uploadFileToS3(
              file,
              presigned.presignedUrl,
              fileInfo?.contentType || "application/octet-stream",
              presigned.requiresServerSideEncryption,
              (progress) => {
                updateFileProgress(file.name, { progress });
              },
              abortControllerRef.current?.signal,
            );

            updateFileProgress(file.name, {
              progress: 100,
              status: "completed",
            });

            return presigned.uploadId;
          } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
              throw error;
            }
            updateFileProgress(file.name, {
              status: "error",
              error: error instanceof Error ? error.message : "Upload failed",
            });
            throw error;
          }
        });

        const uploadIds = await Promise.all(uploadPromises);

        setState((prev) => ({
          ...prev,
          isUploading: false,
          overallProgress: 100,
        }));

        return {
          usedPresigned: true,
          uploadIds,
          files,
        };
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          toast.info("Upload cancelled");
        } else {
          toast.error("Failed to upload files");
          console.error("Presigned upload error:", error);
        }

        setState((prev) => ({
          ...prev,
          isUploading: false,
        }));

        throw error;
      } finally {
        abortControllerRef.current = null;
      }
    },
    [updateFileProgress],
  );

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  const reset = useCallback(() => {
    setState({
      isUploading: false,
      files: [],
      overallProgress: 0,
    });
  }, []);

  return {
    state,
    uploadFiles,
    cancel,
    reset,
  };
}
